import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { GAME_CONTAINER_OFFSET_CONFIG } from "../../../Games/Main/GameContainer/GameContainer";

// Fixed pixel offset to fine-tune background vertical alignment relative to the reelset
// Negative = push background UP, Positive = push background DOWN
const BACKGROUND_Y_OFFSET = -158;
// Manages background spine animations
class BackgroundAnimationController {
  constructor(
    container,
    backgroundSprites,
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
  ) {
    this.container = container;
    this.backgroundSprites = backgroundSprites;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.backgroundSpine = null;
    // Cache for available animations
    this._availableAnimationsCache = null;
    this.initializeBackground();
  }

  // Initialize background spine animation
  initializeBackground() {
    this.backgroundSpine = this.createBackgroundSpine();

    if (!this.backgroundSpine) {
      return;
    }

    // Simple validation
    if (!this.backgroundSpine.skeleton || !this.backgroundSpine.state) {
      return;
    }

    // Invalidate animations cache when spine is recreated
    this._availableAnimationsCache = null;

    // Setup spine - MUST be behind background images
    this.backgroundSpine.position.set(0, 0);
    this.backgroundSpine.zIndex = -10; // Behind background images (which are zIndex 0)
    this.backgroundSpine.visible = true;

    // Add to container
    this.container.addChild(this.backgroundSpine);

    // Setup event listener for drop sounds (only if tension is enabled)
    if (FEATURES_CONFIG.TENSION.ENABLED) {
      this.setupDropSoundListener();
    }

    // Apply responsive scaling and positioning
    this.applyResponsiveBackgroundScaling();

    // Auto-play front_mask on track 2 (intro popup is active at game start)
    this.playFrontMask();
  }

  /**
   * Setup event listener for drop sounds from spine animation events
   * Plays a random drop sound (drop_1 through drop_12) when drop event fires
   */
  setupDropSoundListener() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    // Listen for events from the spine animation
    this.backgroundSpine.state.addListener({
      event: (entry, event) => {
        // Check if the event name contains "drop" (case insensitive)
        if (event && event.data && event.data.name) {
          const eventName = event.data.name.toLowerCase();
          if (eventName.includes("drop") || eventName === "water_drop") {
            this.playRandomDropSound();
          }
        }
      },
    });
  }

  /**
   * Plays a random drop sound from drop_1 to drop_12
   */
  playRandomDropSound() {
    // Generate random number between 1 and 12
    const randomDropNumber = Math.floor(Math.random() * 12) + 1;
    const dropSoundName = `drop_${randomDropNumber}`;

    // Use soundManager to respect sound settings (including background music requirement)
    soundManager.play(SOUND_NAME[dropSoundName]);
  }

  // Create background spine instance
  createBackgroundSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "background_animation-skeleton";
      const atlasAlias = "background_animation-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      // Create independent spine instance
      const backgroundSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      if (
        !backgroundSpine ||
        !backgroundSpine.skeleton ||
        !backgroundSpine.state
      ) {
        return null;
      }

      // Verify skeleton has required data
      if (
        !backgroundSpine.skeleton.data ||
        !backgroundSpine.skeleton.data.animations
      ) {
        return null;
      }

      return backgroundSpine;
    } catch (error) {
      return null;
    }
  }

  // Play base_idle animation (for base game)
  playBaseIdle() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound if it's playing (only if tension is enabled)
      if (FEATURES_CONFIG.TENSION.ENABLED) {
        soundManager.fadeOut(SOUND_NAME.tension_shake, 500);
      }

      // Show spine for base game
      this.backgroundSpine.visible = true;
      // Only clear track 0 (idle animation), leave track 1 (tension) intact
      this.backgroundSpine.state.clearTrack(0);
      this.backgroundSpine.state.setAnimation(0, "base_idle", true);
    } catch (error) {
      // Fail silently
    }
  }

  // Play feature_idle animation (for Hold and Win feature)
  playFeatureIdle() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound if it's playing (only if tension is enabled)
      if (FEATURES_CONFIG.TENSION.ENABLED) {
        soundManager.fadeOut(SOUND_NAME.tension_shake, 500);
      }

      // Keep spine visible - it should always be visible
      // Only clear track 0 (idle animation), leave track 1 (tension) intact
      this.backgroundSpine.state.clearTrack(0);
      this.backgroundSpine.state.setAnimation(0, "feature_idle", true);
    } catch (error) {
      // Silent fail
    }
  }

  // Play base_event intro animation on track 1 (tension start)
  // Track 0 continues playing base_idle/feature_idle without interruption
  playBaseEventIn() {
    // Skip if tension is disabled
    if (!FEATURES_CONFIG.TENSION.ENABLED) {
      return;
    }

    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Start looping tension shake sound
      soundManager.play(SOUND_NAME.tension_shake);

      const animations = this.getAvailableAnimations();

      // Play tension animation on track 1 ONLY (track 0 stays on whatever idle is already playing)
      // The React component handles switching between base_idle/feature_idle based on holdAndWinActive
      if (
        animations.includes("base_event_in") &&
        animations.includes("base_event_idle")
      ) {
        this.backgroundSpine.state.setAnimation(1, "base_event_in", false);
        this.backgroundSpine.state.addAnimation(1, "base_event_idle", true, 0);
      } else if (animations.includes("base_event_idle")) {
        // Fallback: just play loop if intro not available
        this.backgroundSpine.state.setAnimation(1, "base_event_idle", true);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Play base_event exit animation on track 1 (tension end)
  // Track 0 continues playing base_idle/feature_idle without interruption
  playBaseEventOut() {
    // Skip if tension is disabled
    if (!FEATURES_CONFIG.TENSION.ENABLED) {
      return;
    }

    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound
      soundManager.fadeOut(SOUND_NAME.tension_shake, 500);

      const currentTrack = this.backgroundSpine.state.tracks[1];
      const currentAnimation = currentTrack?.animation?.name;

      // Check if we're currently playing base_event animations on track 1
      const isPlayingBaseEvent =
        currentAnimation === "base_event_in" ||
        currentAnimation === "base_event_idle";

      if (!isPlayingBaseEvent) {
        return;
      }

      const animations = this.getAvailableAnimations();

      // Play exit animation on track 1 if available
      if (animations.includes("base_event_out")) {
        this.backgroundSpine.state.setAnimation(1, "base_event_out", false);
        // Track 1 will be empty after exit animation completes (track 0 continues idle)
      } else {
        // Fallback: just clear track 1 if exit animation not available
        this.backgroundSpine.state.clearTrack(1);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Play front_mask animation on track 2 (flowers/tree overlay for intro popup)
  // Track 0 (idle) and track 1 (tension) continue unaffected
  playFrontMask() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      const animations = this.getAvailableAnimations();
      if (animations.includes("front_mask")) {
        this.backgroundSpine.state.setAnimation(2, "front_mask", true);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // Stop front_mask animation and hide all its slots
  stopFrontMask() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      this.backgroundSpine.state.clearTrack(2);

      // Null out all slot attachments used by front_mask so they become invisible
      const frontMaskSlots = [
        "bg_flower_01",
        "bg_flower_02",
        "bg_flower_03",
        "bg_flower_04",
        "bg_flower_07",
        "bg_flower_08",
        "tree_mask",
      ];
      const skeleton = this.backgroundSpine.skeleton;
      frontMaskSlots.forEach((slotName) => {
        const slot = skeleton.findSlot(slotName);
        if (slot) {
          slot.setAttachment(null);
        }
      });
    } catch (error) {
      // Fail silently
    }
  }

  // Apply responsive scaling and positioning to maintain stone ledge alignment
  applyResponsiveBackgroundScaling() {
    if (!this.dimensions) return;

    const { width, height } = this.dimensions;

    // Only scale background when symbols start shrinking (scaleFactor < 1)
    const backgroundScale = Math.min(1, this.symbolScale);

    const centerX = width / 2;

    // Follow GameContainer offset (so background moves with reels)
    const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[this.device];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[this.orientation] ?? 0);
    const gameContainerOffset = this.symbolHeight * offsetMultiplier;

    // Stone ledge offset - scale proportionally with background
    const scaledOffset = BACKGROUND_Y_OFFSET * backgroundScale;
    const positionY = height / 2 - gameContainerOffset + scaledOffset;

    // Apply unified scaling and positioning to all 4 quadrant sprites
    if (this.backgroundSprites) {
      const sprites = [
        this.backgroundSprites.topLeft,
        this.backgroundSprites.topRight,
        this.backgroundSprites.bottomLeft,
        this.backgroundSprites.bottomRight,
      ];

      sprites.forEach((spriteRef) => {
        const sprite = spriteRef?.current || spriteRef;
        if (sprite && sprite.scale && sprite.position) {
          sprite.scale.set(backgroundScale);
          sprite.position.set(centerX, positionY);
        }
      });
    }

    // Spine uses same scale and position — they always move together
    if (this.backgroundSpine) {
      this.backgroundSpine.scale.set(backgroundScale);
      this.backgroundSpine.position.set(centerX, positionY);
    }
  }

  // Update dimensions and reapply scaling
  resizeBackground(
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
  ) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.applyResponsiveBackgroundScaling();
  }

  // Get list of available animation names
  // Result is cached to avoid repeated array mapping
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    if (!this.backgroundSpine?.skeleton?.data?.animations) {
      this._availableAnimationsCache = [];
      return [];
    }

    this._availableAnimationsCache =
      this.backgroundSpine.skeleton.data.animations.map((anim) => anim.name);
    return this._availableAnimationsCache;
  }
}

export default BackgroundAnimationController;
